import { LoadingButton } from "@mui/lab";
import BlockIcon from "@mui/icons-material/Block";
import { useRecordContext, useRefresh } from "react-admin";
import { DateTime } from "luxon";
import { Student } from "../providers/studentsProvider";
import { useDialog } from "../hooks/useDialog";
import { useState } from "react";
import type { EmailDraft } from "../generated/backendClient";
import { useMutation } from "react-query";
import { draftDunningNotice, postponeDunningProcess } from "../api/backend.api";
import { LocalizedError } from "../utils/LocalizedError";
import { reportError } from "../backoffice.utils";
import { SendDunningNoticeDialog, SendDunningNoticeFormValues } from "../dialogs/SendDunningNoticeDialog";
import { serverAPI } from "../api/server.api";
import { Button } from "@mui/material";

export function BlockStudentAndSendPaymentReminderButton({
  variant = "rowButton",
}: {
  variant?: "rowButton" | "dialogButton";
}) {
  const student = useRecordContext<Student>();
  const refresh = useRefresh();
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [dunningProcessId, setDunningProcessId] = useState<string>();
  const [draft, setDraft] = useState<EmailDraft | undefined>();

  const draftEmailAndOpenDialog = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftingEmail(true);
    try {
      const { dunningProcess, draft } = await draftDunningNotice(student, "student_blocked");
      setDunningProcessId(dunningProcess.id);
      setDraft(draft);
      openDialog();
    } catch (error) {
      if (error instanceof LocalizedError) {
        showErrorToast(error.message);
      } else {
        reportError("Failed to draft payment reminder", error);
        showErrorToast(
          "Oje, es ist ein Fehler beim Vorbereiten der Zahlungserinnerung-E-Mail aufgetreten. Bitte wende Dich an die Technik.",
        );
      }
    } finally {
      setDraftingEmail(false);
    }
  };

  const { mutateAsync: blockStudentAndSendPaymentReminder } = useMutation(
    async ({ emailSubject, emailText, postponementDate }: SendDunningNoticeFormValues) => {
      if (!dunningProcessId) {
        throw new Error("dunningProcessId not set");
      }
      await serverAPI.sendDunningNotice({
        student,
        dunningProcessId,
        noticeType: "student_blocked",
        emailSubject,
        emailText,
      });
      await postponeDunningProcess(dunningProcessId, student.id, postponementDate);
    },
    {
      onSuccess: () => {
        refresh();
        showSuccessToast("Zahlungserinnerung-E-Mail erfolgreich versandt, Fahrschüler wurde geblockt.");
        closeDialog();
      },
      onError: (error) => {
        reportError("Failed to send warn email", error);
        showErrorToast("Fehler beim Versenden der Zahlungserinnerung-E-Mail");
      },
    },
  );

  return (
    <>
      {variant === "rowButton" ? (
        <LoadingButton
          variant="outlined"
          loading={draftingEmail}
          startIcon={<BlockIcon />}
          onClick={draftEmailAndOpenDialog}
        >
          Blockieren und Zahlungserinnerung senden
        </LoadingButton>
      ) : (
        <Button variant="text" disableRipple sx={{ height: "20px" }} onClick={draftEmailAndOpenDialog}>
          Schüler blockieren
        </Button>
      )}
      <SendDunningNoticeDialog
        title="Zahlungserinnerung"
        buttonLabel="Senden und Fahrschüler blockieren"
        {...dialogProps}
        initialFormValues={{
          emailSubject: draft?.subject ?? "",
          emailText: draft?.text ?? "",
          postponementDate: DateTime.now().plus({ days: 7 }),
        }}
        onSubmit={blockStudentAndSendPaymentReminder}
      />
    </>
  );
}
